import { useEffect } from 'react'

import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { selectNavigation } from '../state/navigation'

import {
  AllAchievements,
  UserAchievements
} from './Achievements'

import { TodayHome } from './Today'

import {
  EventsAssigned,
  EventsFavorite,
  EventsHome
} from './Events'

import {
  YapsHome
} from './Yaps'

import { MapConsite, MapParking } from './Map'

import { ConinfoBar, ConinfoMessages, ConinfoPhotos } from './Coninfo'

import { FursuitVotingHome } from './FursuitVoting'
import { SnacksExchange } from './SnacksExchange'
import { SnacksExchangeVoting } from './SnacksExchangeVoting'
import { SnacksResults } from './SnacksResults'

import { AuthError, Login, Logout, NotRegisteredError } from './Login'
import { WidgetsIcons } from './Widgets'

import { TearoomHome } from './Tearoom'
import { fetchTearoomMenu } from '../api/tearoom'
import { fetchAchievements, fetchUserAchievements } from '../api/achievements'
import {
  fetchConinfoBar,
  fetchConinfoMessages,
  fetchConinfoPhotos
} from '../api/coninfo'
import { fetchEvents, fetchUserEvents } from '../api/events'
import { fetchMap } from '../api/map'
import { fetchAreas } from '../api/parking'
import { fetchNotifications } from '../api/notifications'
import { fetchOwnRegistration, fetchRegistrations, fetchSnacksResults } from '../api/snacks'
import { fetchUserVotingCategory } from '../api/voting'
import { fetchYaps } from '../api/yaps'

import { useDispatch, useSelector } from 'react-redux'
import { requestWithUserAuth } from '../api/auth'
import { fetchConfig } from '../api/config'
import { setAccorditionReset } from '../state/events'
import { syncFavoritesOnLoad } from '../state/events/favoritesThunks'
import { getConventionYear } from '../state/events/favoritesStorage'
import { setLanguageDataRefresh } from '../state/lang'
import { isDefined, notDefined } from '../utils'

const fetchRoute = {
  '/today': {
    navigation: { footerIndex: 0 },
    fetcher: (config, _auth, language) =>
      Promise.all([fetchNotifications(config), fetchConinfoMessages(config, language)])
  },
  '/events': {
    navigation: { footerIndex: 1 },
    fetcher: (config, auth, language, dispatch) => {
      const year = getConventionYear(config)
      dispatch(syncFavoritesOnLoad(config, auth, year))
      return requestWithUserAuth(
        config,
        auth,
        (authed) => fetchUserEvents(config, authed, language),
        () => fetchEvents(config)
      )
    },
    dispatcher: (navigation, dispatch) => {
      const { footerIndex } = navigation
      if (footerIndex !== 1) {
        dispatch(setAccorditionReset())
      }
    }
  },
  '/events/favorite': {
    navigation: { footerIndex: 1 },
    fetcher: (config, auth, language, dispatch) => {
      const year = getConventionYear(config)
      dispatch(syncFavoritesOnLoad(config, auth, year))
      return requestWithUserAuth(
        config,
        auth,
        (authed) => fetchUserEvents(config, authed, language),
        () => fetchEvents(config)
      )
    }
  },
  '/events/assigned': {
    navigation: { footerIndex: 1 },
    fetcher: (config, auth, language) =>
      requestWithUserAuth(
        config,
        auth,
        (authed) => fetchUserEvents(config, authed, language),
        () => fetchEvents(config)
      )
  },
  '/map/consite': {
    navigation: { footerIndex: 2 },
    fetcher: (config) => fetchMap(config)
  },
  '/map/parking': {
    navigation: { footerIndex: 2 },
    fetcher: (config) => fetchAreas(config)
  },
  '/coninfo': {
    navigation: { footerIndex: 3 },
    fetcher: (config, _auth, language) => fetchConinfoMessages(config, language)
  },
  '/coninfo/messages': {
    navigation: { footerIndex: 3 },
    fetcher: (config, _auth, language) => fetchConinfoMessages(config, language)
  },
  '/coninfo/photos': {
    navigation: { footerIndex: 3 },
    fetcher: (config) => fetchConinfoPhotos(config)
  },
  '/coninfo/bar': {
    navigation: { footerIndex: 3 },
    fetcher: (config, _auth, language) => fetchConinfoBar(config, language)
  },
  '/achievements': {
    navigation: {},
    fetcher: (config, auth) => {
      fetchAchievements(config)
      requestWithUserAuth(config, auth, (authed) =>
        fetchUserAchievements(config, authed)
      )
    }
  },
  '/achievements/user': {
    navigation: {},
    fetcher: (config, auth) => {
      requestWithUserAuth(config, auth, (authed) =>
        fetchUserAchievements(config, authed)
      )
    }
  },
  '/yaps': {
    navigation: { footerIndex: 0 },
    fetcher: (config, auth, _language) =>
      requestWithUserAuth(config, auth, (authed) =>
        fetchYaps(config, authed, 0, 30)
      )
  },
  '/voting': {
    navigation: {},
    fetcher: (config, auth) =>
      requestWithUserAuth(config, auth, (authed) =>
        fetchUserVotingCategory(config, authed, 'fursuit')
      )
  },
  '/voting/snacks': {
    navigation: {},
    fetcher: (config, auth) =>
      requestWithUserAuth(config, auth, (authed) => {
        fetchRegistrations(config, authed)
        fetchUserVotingCategory(config, authed, 'snacks_exchange')
      })
  },
  '/voting/snacks/my-table': {
    navigation: {},
    fetcher: (config, auth) =>
      requestWithUserAuth(config, auth, (authed) => {
        fetchOwnRegistration(config, authed)
        fetchRegistrations(config, authed)
        fetchUserVotingCategory(config, authed, 'snacks_exchange')
      })
  },
  '/tearoom': {
    navigation: {},
    fetcher: (config) => fetchTearoomMenu(config)
  },
  '/voting/snacks/results': {
    navigation: {},
    fetcher: (config, auth) =>
      requestWithUserAuth(config, auth, (authed) =>
        fetchSnacksResults(config, authed)
      )
  }
}

export const Routing = () => {
  const location = useLocation()
  const config = useSelector((state) => state.config)
  const auth = useSelector((state) => state.auth)
  const navigation = useSelector((state) => state.navigation)
  const language = useSelector((state) => state.language)
  const dispatch = useDispatch()

  useEffect(() => {
    try {
      const path = location.pathname

      const { lastLocation } = navigation
      const shouldTransitionAndFetch =
        notDefined(lastLocation) || path !== lastLocation || language.refresh

      if (!shouldTransitionAndFetch) {
        return
      }

      fetchConfig()
      let fetch = fetchRoute[path]
      const parts = path.split('/')
      for (let i = parts.length; i > 0; i--) {
        const key = parts.slice(0, i).join('/')
        fetch = fetchRoute[key]
        if (isDefined(fetch)) {
          break
        }
      }
      if (isDefined(fetch)) {
        if (isDefined(fetch.fetcher)) {
          try {
            fetch.fetcher(config.config, auth, language.active, dispatch)
          } catch (error) {
            console.error(error)
            dispatch(
              selectNavigation({
                footerIndex: -1,
                path: '/error'
              })
            )
          }
        }

        if (isDefined(fetch.dispatcher)) {
          fetch.dispatcher(navigation, dispatch)
        }

        if (isDefined(fetch.navigation)) {
          dispatch(selectNavigation({ ...fetch.navigation, path }))
          dispatch(setLanguageDataRefresh({ refresh: false }))
        } else {
          dispatch(selectNavigation({ footerIndex: -1, path }))
          dispatch(setLanguageDataRefresh({ refresh: false }))
        }
      } else {
        dispatch(selectNavigation({ footerIndex: -1, path }))
        dispatch(setLanguageDataRefresh({ refresh: false }))
      }
    } catch (error) {
      console.error(error)
      dispatch(selectNavigation({ footerIndex: -1, path: '/error' }))
      dispatch(setLanguageDataRefresh({ refresh: false }))
    }
  }, [location, config, auth, navigation, language, dispatch])

  return (
    <Routes>
      <Route exact path="/" element={<Navigate to="/today" />} />
      <Route exact path="/notifications" element={<Navigate to="/today" />} />

      <Route path="today">
        <Route index element={<TodayHome />} />
        <Route path="*" element={<TodayHome />} />
      </Route>

      <Route path="achievements">
        <Route index element={<AllAchievements />} />
        <Route path="user" element={<UserAchievements />} />
      </Route>

      <Route path="yaps">
        <Route index element={<YapsHome />} />
      </Route>

      <Route path="events">
        <Route index element={<EventsHome />} />
        <Route path="favorite" element={<EventsFavorite />} />
        <Route path="assigned" element={<EventsAssigned />} />
      </Route>

      <Route path="map">
        <Route index element={<Navigate to="/map/consite" />} />
        <Route path="consite" element={<MapConsite />} />
        <Route path="parking" element={<MapParking />} />
      </Route>

      <Route path="voting">
        <Route index element={<FursuitVotingHome />} />
        <Route path="snacks">
          <Route index element={<SnacksExchange />} />
          <Route path="my-table" element={<SnacksExchange />} />
          <Route path="results" element={<SnacksResults />} />
          <Route path=":tableNumber" element={<SnacksExchangeVoting />} />
        </Route>
      </Route>

      <Route path="login">
        <Route index element={<Login />} />
      </Route>

      <Route path="authError">
        <Route index element={<AuthError />} />
      </Route>

      <Route path="notRegistered">
        <Route index element={<NotRegisteredError />} />
      </Route>

      <Route path="coninfo">
        <Route index element={<ConinfoMessages />} />
        <Route path="messages" element={<ConinfoMessages />} />
        <Route path="photos" element={<ConinfoPhotos />} />
        <Route path="bar" element={<ConinfoBar />} />
      </Route>

      <Route path="tearoom">
        <Route index element={<TearoomHome />} />
      </Route>

      <Route path="logout">
        <Route index element={<Logout />} />
      </Route>

      <Route path="widgets">
        <Route path="icons" element={<WidgetsIcons />} />
      </Route>
    </Routes>
  )
}
